import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import MoneyInput from '../components/ui/MoneyInput';
import {
  Shield, ArrowRight, Globe, Car,
  Briefcase, Package, Target, Users, Award, Lock, Home as HomeIcon,
  Code, Gem, Music, ShoppingBag
} from 'lucide-react';
import { publicAPI } from '../services/api';

const ICON_MAP = {
  domain_website: Globe, xe_co: Car, freelance: Briefcase,
  hang_hoa: Package, tai_khoan_tmdt: Target, phan_mem_license: Code,
  bat_dong_san: HomeIcon, do_suu_tam: Gem, giai_tri_media: Music, khac: ShoppingBag,
};

const DEFAULT_HOME_CATS = [
  { key: 'domain_website',   name: 'Tên Miền & Website' },
  { key: 'xe_co',            name: 'Xe Cộ'              },
  { key: 'freelance',        name: 'Freelance'           },
  { key: 'hang_hoa',         name: 'Hàng Hóa'           },
  { key: 'tai_khoan_tmdt',   name: 'Tài Khoản TMDT'     },
  { key: 'phan_mem_license', name: 'Phần Mềm'            },
];

const Home = () => {
  const navigate = useNavigate();
  const [transactionType, setTransactionType] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [amountErr, setAmountErr] = useState('');
  const [stats, setStats] = useState(null);
  const [apiCategories, setApiCategories] = useState(DEFAULT_HOME_CATS);

  // Set initial transactionType from defaults immediately (no flash)
  useEffect(() => { setTransactionType(DEFAULT_HOME_CATS[0].key); }, []);

  // Defer API calls until after LCP using requestIdleCallback
  useEffect(() => {
    const load = () => {
      publicAPI.getStats().then(setStats).catch(() => {});
      publicAPI.getCategories()
        .then(data => {
          if (data?.categories?.length) {
            const cats = data.categories.map(c => ({ key: c.key, name: c.name, description: c.description || '' }));
            setApiCategories(cats);
            setTransactionType(cats[0].key);
          }
        })
        .catch(() => {});
    };
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(load, { timeout: 2000 })
      : setTimeout(load, 500);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  const handleStart = () => {
    const numAmt = Number(amount);
    if (!amount || isNaN(numAmt) || numAmt <= 0) {
      setAmountErr('Vui lòng nhập số tiền giao dịch');
      return;
    }
    setAmountErr('');
    navigate(`/create-transaction?category=${encodeURIComponent(transactionType)}&amount=${encodeURIComponent(amount)}&currency=${encodeURIComponent(currency)}`);
  };

  const steps = [
    { n: 1, title: 'Thỏa thuận',   desc: 'Hai bên đồng ý điều khoản giao dịch' },
    { n: 2, title: 'Thanh toán',   desc: 'Người mua nạp tiền vào tài khoản ký quỹ GDTG' },
    { n: 3, title: 'Giao hàng',    desc: 'Người bán thực hiện giao hàng / dịch vụ' },
    { n: 4, title: 'Xác nhận',     desc: 'Người mua kiểm tra và xác nhận đã nhận' },
    { n: 5, title: 'Giải ngân',    desc: 'GDTG chuyển tiền an toàn cho người bán' },
  ];

  const features = [
    { icon: Lock,  title: 'Ký quỹ minh bạch', desc: 'Tiền giữ đến khi bàn giao đúng thỏa thuận; kết nối HTTPS' },
    { icon: Shield, title: 'Phí rõ ràng', desc: 'Phí khoảng 3%, hai bên thỏa thuận ai chịu khi tạo đơn' },
    { icon: Users, title: 'Hỗ trợ 24/7', desc: 'Email, Telegram @trunggian247com và chat trên giao dịch' },
    { icon: Award, title: 'Tranh chấp có quy trình', desc: 'Xem xét bằng chứng trong khoảng 48 giờ khi đủ hồ sơ' },
  ];

  const statItems = [
    { value: '~3%', label: 'Phí dịch vụ' },
    { value: '5 bước', label: 'Quy trình ký quỹ' },
    { value: '~48h', label: 'Xem tranh chấp' },
    { value: 'VNĐ/USDT', label: 'Thanh toán' },
  ];

  const p0Guides = [
    { to: '/blog/phi-giao-dich-trung-gian', title: 'Phí giao dịch trung gian', desc: 'Cách tính phí ~3%, ai chịu phí, ví dụ thực tế' },
    { to: '/blog/ky-quy-online-la-gi', title: 'Ký quỹ online là gì?', desc: 'Khái niệm escrow, quy trình 5 bước, so sánh' },
    { to: '/blog/escrow-la-gi', title: 'Escrow là gì?', desc: 'Giải thích dịch vụ giữ tiền trung gian, khi nào nên dùng' },
    { to: '/blog/trung-gian-mua-ban-domain', title: 'Trung gian mua bán domain', desc: 'Escrow tên miền .vn / quốc tế an toàn' },
    { to: '/blog/trung-gian-mua-ban-website', title: 'Trung gian mua bán website', desc: 'Checklist due diligence + giải ngân có điều kiện' },
    { to: '/blog/trung-gian-mua-ban-usdt-p2p', title: 'Trung gian mua bán USDT P2P', desc: 'Ký quỹ OTC ngoài sàn, checklist TRC20/BEP20' },
  ];

  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Trunggian247",
      "alternateName": ["Trung Gian 247", "GDTG"],
      "url": "https://trunggian247.com",
      "logo": "https://trunggian247.com/logo512.svg",
      "description": "Nền tảng ký quỹ trực tuyến tại Việt Nam. Bảo vệ người mua và người bán, hỗ trợ VND/USDT, phí khoảng 3%.",
      "email": "support.trunggian247com@gmail.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "availableLanguage": ["Vietnamese"],
        "email": "support.trunggian247com@gmail.com",
        "hoursAvailable": "Mo-Su 00:00-24:00",
        "url": "https://t.me/trunggian247com",
      },
      "sameAs": [
        "https://www.facebook.com/trunggian247com",
        "https://t.me/trunggian247com",
      ],
      "areaServed": "VN",
      "knowsAbout": ["giao dịch trung gian", "escrow", "ký quỹ online", "USDT P2P"],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Hướng dẫn ký quỹ Trunggian247",
      "itemListElement": p0Guides.map((g, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://trunggian247.com${g.to}`,
        "name": g.title,
      })),
    },
  ];

  return (
    <>
    <SEOHead
      title="Giao dịch trung gian an toàn — Ký quỹ VNĐ/USDT, phí ~3% | Trunggian247"
      description="Giao dịch trung gian an toàn với Trunggian247: ký quỹ VNĐ/USDT, phí khoảng 3%, bảo vệ người mua và người bán, hỗ trợ tranh chấp khoảng 48 giờ."
      canonical="/"
      jsonLd={homeJsonLd}
    />
    <div className="min-h-screen pb-16 sm:pb-0">

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50 py-10 sm:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-8">

            <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-3 py-1 text-xs sm:text-sm">
              Dịch vụ ký quỹ trực tuyến uy tín
            </Badge>

            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Giao dịch trung gian
              <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                an toàn với ký quỹ
              </span>
            </h1>

            <p className="text-sm sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Trunggian247 giữ tiền đến khi bàn giao xong. Thanh toán VNĐ/USDT, phí khoảng 3%, hỗ trợ tranh chấp khoảng 48 giờ.
            </p>

            {/* Quick Start Form — native selects (no Radix) for max performance */}
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg border-2 border-gray-100 p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-3">Bắt đầu giao dịch ngay</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3">
                <select
                  aria-label="Loại giao dịch"
                  value={transactionType}
                  onChange={e => setTransactionType(e.target.value)}
                  className="h-9 text-sm px-3 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {apiCategories.map(c => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
                <MoneyInput
                  currency={currency === 'USD' ? 'USDT' : 'VND'}
                  value={amount}
                  onChange={raw => { setAmount(raw); if (raw > 0) setAmountErr(''); }}
                  className={`h-9 text-sm ${amountErr ? 'border-red-400' : ''}`}
                  placeholder="Số tiền"
                />
                <select
                  aria-label="Loại tiền tệ"
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  className="h-9 text-sm px-3 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="VND">VND</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              {amountErr && <p className="text-red-500 text-xs mt-1">{amountErr}</p>}
              <Button onClick={handleStart}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-12 text-sm sm:text-base font-semibold">
                Bắt đầu giao dịch <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 pt-2 sm:pt-8">
              {statItems.map((s, i) => (
                <div key={i} className="space-y-1">
                  <p className="text-xl sm:text-3xl font-bold text-blue-600">{s.value}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────── */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">Cách thức hoạt động</h2>
            <p className="text-sm sm:text-xl text-gray-600">Quy trình đơn giản trong 5 bước</p>
          </div>

          {/* Mobile: 2-column grid — all 5 steps visible, no scroll */}
          <div className="grid grid-cols-2 gap-3 md:hidden">
            {steps.map((step) => (
              <div key={step.n}
                className={`flex items-start gap-3 bg-blue-50 rounded-xl p-3 ${step.n === 5 ? 'col-span-2' : ''}`}>
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {step.n}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 leading-tight">{step.title}</p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: row */}
          <div className="hidden md:block max-w-5xl mx-auto">
            <div className="relative">
              <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200" />
              <div className="grid grid-cols-5 gap-6 relative">
                {steps.map((step) => (
                  <div key={step.n} className="text-center">
                    <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-2xl font-bold mb-4 shadow-lg hover:scale-110 transition-transform">
                      {step.n}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm">{step.title}</h3>
                    <p className="text-xs text-gray-600">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Categories ─────────────────────────────────────────────────── */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">Danh mục giao dịch</h2>
            <p className="text-sm sm:text-xl text-gray-600">Hỗ trợ đa dạng loại hình giao dịch</p>
          </div>

          {/* Mobile: 3-col grid */}
          <div className="grid grid-cols-3 sm:hidden gap-2">
            {apiCategories.slice(0, 6).map((cat) => {
              const Icon = ICON_MAP[cat.key] || ShoppingBag;
              return (
                <div key={cat.key}
                  onClick={() => navigate(`/create-transaction?category=${encodeURIComponent(cat.key)}`)}
                  className="bg-white rounded-xl p-2.5 text-center cursor-pointer border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all active:scale-95">
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 mb-1.5 group-hover:bg-blue-600 transition-colors">
                    <Icon className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-xs font-semibold text-gray-900 leading-tight">{cat.name}</p>
                </div>
              );
            })}
          </div>

          {/* Desktop: 3-col → up to 6-col */}
          <div className="hidden sm:grid grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            {apiCategories.slice(0, 6).map((cat) => {
              const Icon = ICON_MAP[cat.key] || ShoppingBag;
              return (
                <Card key={cat.key}
                  className="group hover:shadow-xl transition-all cursor-pointer border-2 hover:border-blue-400 hover:-translate-y-1"
                  onClick={() => navigate(`/create-transaction?category=${encodeURIComponent(cat.key)}`)}>
                  <CardContent className="p-6 text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 group-hover:bg-blue-600 transition-colors">
                      <Icon className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                    {cat.description && <p className="text-sm text-gray-600">{cat.description}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────── */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">Tại sao chọn Trunggian247?</h2>
            <p className="text-sm sm:text-xl text-gray-600">Cam kết bảo vệ quyền lợi của bạn</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8 max-w-6xl mx-auto">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="text-center space-y-2 sm:space-y-4 p-3 sm:p-6 rounded-xl hover:bg-blue-50 transition-colors">
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-blue-100">
                    <Icon className="h-5 w-5 sm:h-8 sm:w-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-lg">{f.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-base hidden sm:block">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── P0 guides (internal links) ─────────────────────────────────── */}
      <section className="py-10 sm:py-16 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Kiến thức ký quỹ cần đọc</h2>
            <p className="text-sm sm:text-base text-gray-600">Hướng dẫn chi tiết trước khi tạo giao dịch trên Trunggian247</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {p0Guides.map((g) => (
              <Link
                key={g.to}
                to={g.to}
                className="block rounded-xl border border-gray-200 p-5 hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
              >
                <h3 className="font-semibold text-blue-800 mb-1">{g.title}</h3>
                <p className="text-sm text-gray-600">{g.desc}</p>
              </Link>
            ))}
          </div>
          <p className="text-center mt-6">
            <Link to="/blog" className="text-sm font-medium text-blue-700 hover:underline">
              Xem tất cả bài trên Blog →
            </Link>
          </p>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section className="py-10 sm:py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-3">Sẵn sàng bắt đầu?</h2>
          <p className="text-sm sm:text-xl mb-6 opacity-90 max-w-2xl mx-auto">
            Tham gia cùng {stats ? stats.total_users?.toLocaleString('vi-VN') : 'hàng nghìn'} người dùng tin tưởng GDTG
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-6 py-4 sm:px-8 sm:py-6 text-sm sm:text-lg"
              onClick={() => navigate('/register')}>
              Đăng ký ngay <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button size="lg" variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-6 py-4 sm:px-8 sm:py-6 text-sm sm:text-lg"
              onClick={() => navigate('/how-it-works')}>
              Tìm hiểu thêm
            </Button>
          </div>
        </div>
      </section>

      {/* ── SEO Internal Links ─────────────────────────────────────────── */}
      <section className="py-8 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-xs text-gray-700 mb-4 uppercase tracking-wider font-semibold">Khám phá thêm</p>
          <nav className="flex flex-wrap justify-center gap-3 sm:gap-6 text-sm">
            <Link to="/how-it-works" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Cách hoạt động</Link>
            <Link to="/categories"   className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Danh mục giao dịch</Link>
            <Link to="/blog"         className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Blog & Hướng dẫn</Link>
            <Link to="/blog/phi-giao-dich-trung-gian" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Phí trung gian</Link>
            <Link to="/blog/ky-quy-online-la-gi" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Ký quỹ online</Link>
            <Link to="/blog/escrow-la-gi" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Escrow là gì</Link>
            <Link to="/blog/trung-gian-mua-ban-domain" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Mua bán domain</Link>
            <Link to="/blog/trung-gian-mua-ban-website" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Mua bán website</Link>
            <Link to="/blog/trung-gian-mua-ban-usdt-p2p" className="text-blue-700 hover:text-blue-900 hover:underline font-medium">USDT P2P</Link>
            <Link to="/faq"          className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Câu hỏi thường gặp</Link>
            <Link to="/contact"      className="text-blue-700 hover:text-blue-900 hover:underline font-medium">Liên hệ hỗ trợ</Link>
            <a href="https://vnpay.vn" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 hover:underline">VNPAY</a>
            <a href="https://www.sbv.gov.vn" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 hover:underline">Ngân hàng Nhà nước</a>
          </nav>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
